/**
 * People joining: the accept page, invitations in a tenant, and tenants on
 * the platform.
 *
 * PLAN-2026-09-15 B3. The API routes and the reasons behind each rule are in
 * `src/api/server.ts` under "People joining"; migration 035 explains the link.
 *
 * 🔴 An invitation link is shown ONCE, to the person who made it. Only its hash
 * is stored, so nothing can show it again — the screen says so beside it rather
 * than letting someone close the drawer expecting to come back for it.
 */

import { Check, Copy, Moon, Plus, Sun } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, api } from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  Drawer,
  Empty,
  Field,
  Loading,
  Pill,
  Section,
  Toolbar,
  type ColumnDef,
  type PillState,
} from './components/index.ts';

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

/** The absolute link for a token, on this console. */
export function inviteLink(token: string): string {
  return `${window.location.origin}${window.location.pathname}#/invite/${token}`;
}

/** Whether the server emailed the link (2026-10-21), as the invite routes answer it. */
export type InviteEmail = { state: 'sent' } | { state: 'failed' | 'off'; reason: string };

/**
 * What happened to the email, above the link. The link stays either way: a
 * mail can land in spam, and an unsent one has to be sent by hand.
 */
function EmailOutcome({ email, to }: { email: InviteEmail | null; to: string }): React.JSX.Element {
  if (email === null) return <Banner tone="ok" title="Invitation created">Send them this link.</Banner>;
  if (email.state === 'sent') {
    return (
      <Banner tone="ok" title={`Emailed to ${to}`}>
        The link below is the same one, in case the email does not arrive. Inviting again later replaces it.
      </Banner>
    );
  }
  return (
    <Banner tone="warn" title={email.state === 'failed' ? 'The invitation was created, but the email failed' : 'Not emailed'}>
      {email.reason}. Copy the link below and send it to {to} yourself.
    </Banner>
  );
}

function CopyLink({ token }: { token: string }): React.JSX.Element {
  const link = inviteLink(token);
  const [copied, setCopied] = useState(false);
  return (
    <div className="stack-tight invite-link">
      <input readOnly value={link} aria-label="Invitation link" onFocus={(e) => e.currentTarget.select()} />
      <Button
        size="sm"
        icon={copied ? <Check size={14} /> : <Copy size={14} />}
        onClick={() => {
          void navigator.clipboard?.writeText(link).then(() => setCopied(true));
        }}
      >
        {copied ? 'Copied' : 'Copy link'}
      </Button>
      <span className="small muted">
        This link is shown once and cannot be read again. It works for 7 days, for this address only.
      </span>
    </div>
  );
}

// ── Accepting ────────────────────────────────────────────────────────────

interface InvitePreview {
  email: string;
  role: string;
  tenant: { slug: string | null; name: string | null };
  state: 'open' | 'expired' | 'accepted' | 'revoked';
  message?: string;
  expiresAt: string;
  accountExists: boolean;
}

export function AcceptInvite({
  token,
  theme,
  onToggleTheme,
  onDone,
}: {
  token: string;
  theme: string;
  onToggleTheme: () => void;
  /** Leave the invitation page for the sign-in screen. */
  onDone: () => void;
}): React.JSX.Element {
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .get<InvitePreview>(`/api/auth/invites/${encodeURIComponent(token)}`)
      .then((p) => live && setPreview(p))
      .catch((err: unknown) => live && setError(failure(err)));
    return (): void => {
      live = false;
    };
  }, [token]);

  const tenantName = preview?.tenant.name ?? 'this tenant';

  return (
    <div className="centre">
      <div className="card">
        <div className="row centre-head">
          <span className="brand-inline">
            <span className="mark" aria-hidden="true" />
            <span className="name">COSMON</span>
          </span>
          <span className="spacer" />
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            icon={theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            onClick={onToggleTheme}
          >
            {theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          </Button>
        </div>

        {error !== null && preview === null && (
          <>
            <Banner tone="fault" title="This invitation cannot be opened">
              {error}
            </Banner>
            <Button onClick={onDone}>Go to sign in</Button>
          </>
        )}
        {error === null && preview === null && <Loading rows={3} cols={1} label="Opening the invitation" />}

        {preview !== null && accepted && (
          <>
            <h1 className="section-title">You have joined {tenantName}</h1>
            <p className="small muted">
              Sign in as {preview.email}.
              {preview.role === 'client' &&
                ' Your access is through the customer portal; the link to it comes from whoever invited you.'}
            </p>
            <Button variant="primary" onClick={onDone}>
              Go to sign in
            </Button>
          </>
        )}

        {preview !== null && !accepted && preview.state !== 'open' && (
          <>
            <Banner tone="warn" title="This invitation cannot be used">
              {preview.message}
            </Banner>
            <Button onClick={onDone}>Go to sign in</Button>
          </>
        )}

        {preview !== null && !accepted && preview.state === 'open' && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError(null);
              try {
                await api.post(`/api/auth/invites/${encodeURIComponent(token)}/accept`, {
                  password,
                  ...(displayName.trim() !== '' ? { displayName } : {}),
                });
                setAccepted(true);
              } catch (err) {
                setError(failure(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            <h1 className="section-title">Join {tenantName}</h1>
            <p className="small muted invite-lede">
              {preview.email} is invited as <strong>{preview.role}</strong>.{' '}
              {preview.accountExists
                ? 'This address already has an account: enter its password to accept.'
                : 'Choose a password to create your account.'}
            </p>

            {!preview.accountExists && (
              <Field id="invite-name" label="Your name">
                <input
                  id="invite-name"
                  type="text"
                  autoComplete="name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </Field>
            )}
            <Field
              id="invite-password"
              label={preview.accountExists ? 'Password' : 'Choose a password'}
              {...(preview.accountExists ? {} : { hint: 'At least 12 characters.' })}
              error={error}
            >
              <input
                id="invite-password"
                type="password"
                autoComplete={preview.accountExists ? 'current-password' : 'new-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <Button type="submit" variant="primary" busy={busy} disabled={password === ''}>
              {preview.accountExists ? 'Accept' : 'Create account and join'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Invitations in this tenant ───────────────────────────────────────────

interface InviteRow {
  id: string;
  email: string;
  role: string;
  state: 'open' | 'expired' | 'accepted' | 'revoked';
  createdAt: string;
  expiresAt: string;
  invitedBy: string | null;
  acceptedBy: string | null;
}

const STATE_PILL: Record<InviteRow['state'], PillState> = {
  open: 'warning',
  expired: 'stale',
  accepted: 'ok',
  revoked: 'neutral',
};

const INVITE_ROLES = ['admin', 'operator', 'viewer', 'client'] as const;

export function TenantInvites({ callerRole }: { callerRole: string | null }): React.JSX.Element | null {
  const [rows, setRows] = useState<InviteRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const mayInvite = callerRole === 'owner' || callerRole === 'admin';

  const load = useCallback(async () => {
    try {
      setRows((await api.get<{ invites: InviteRow[] }>('/api/users/invites')).invites);
      setError(null);
    } catch (err) {
      setError(failure(err));
    }
  }, []);

  useEffect(() => {
    if (mayInvite) void load();
  }, [load, mayInvite]);

  const columns = useMemo<ColumnDef<InviteRow, unknown>[]>(
    () => [
      { id: 'email', header: 'Address', accessorKey: 'email' },
      { id: 'role', header: 'Role', accessorKey: 'role', size: 100 },
      {
        id: 'state',
        header: 'State',
        accessorKey: 'state',
        cell: ({ row }) => (
          <Pill state={STATE_PILL[row.original.state]}>
            {row.original.state === 'open' ? 'waiting' : row.original.state}
          </Pill>
        ),
        size: 110,
      },
      {
        id: 'by',
        header: 'Sent by',
        accessorFn: (r) => r.invitedBy ?? '',
        cell: ({ row }) => <span className="small muted">{row.original.invitedBy ?? '—'}</span>,
      },
      {
        id: 'when',
        header: 'Expires',
        accessorKey: 'expiresAt',
        cell: ({ row }) => (
          <span className="small muted">
            {row.original.state === 'open' ? new Date(row.original.expiresAt).toLocaleDateString() : '—'}
          </span>
        ),
        size: 110,
      },
      {
        id: 'act',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.state === 'open' ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                try {
                  await api.del(`/api/users/invites/${row.original.id}`);
                  await load();
                } catch (err) {
                  setError(failure(err));
                }
              }}
            >
              Withdraw
            </Button>
          ) : null,
        size: 110,
      },
    ],
    [load],
  );

  if (!mayInvite) return null;

  const open = rows?.filter((r) => r.state === 'open').length ?? 0;

  return (
    <>
      <Toolbar
        end={
          <Button variant="primary" icon={<Plus size={14} />} onClick={() => setInviting(true)}>
            Invite someone
          </Button>
        }
      />
      {error !== null && <Banner tone="fault">{error}</Banner>}
      {rows === null ? (
        <Loading rows={2} cols={4} label="Loading invitations" />
      ) : (
        rows.length > 0 && (
          <Section flush title="Invitations" summary={`${open} waiting to be accepted`}>
            <DataTable
              data={rows}
              columns={columns}
              getRowId={(r) => r.id}
              initialSort={[{ id: 'when', desc: true }]}
              caption="Invitations to this tenant"
            />
          </Section>
        )
      )}
      {inviting && (
        <InviteDrawer
          callerRole={callerRole ?? ''}
          onClose={() => setInviting(false)}
          onCreated={() => void load()}
        />
      )}
    </>
  );
}

function InviteDrawer({
  callerRole,
  onClose,
  onCreated,
}: {
  callerRole: string;
  onClose: () => void;
  onCreated: () => void;
}): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('operator');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [sent, setSent] = useState<InviteEmail | null>(null);

  const roles = callerRole === 'owner' ? ['owner', ...INVITE_ROLES] : INVITE_ROLES;

  return (
    <Drawer open onClose={onClose} title="Invite someone" subtitle="They choose their own password.">
      <div className="form-narrow">
        {token === null ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError(null);
              try {
                const res = await api.post<{ token: string; email?: InviteEmail }>('/api/users/invites', { email, role });
                setToken(res.token);
                setSent(res.email ?? null);
                onCreated();
              } catch (err) {
                setError(failure(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            {error !== null && <Banner tone="fault">{error}</Banner>}
            <Field id="invite-email" label="Email address">
              <input id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field
              id="invite-role"
              label="Role"
              hint={
                role === 'client'
                  ? 'A customer: sees only the sites you grant them, in the portal.'
                  : role === 'owner'
                    ? 'Full control, including removing administrators.'
                    : role === 'admin'
                      ? 'Configuration and people.'
                      : role === 'operator'
                        ? 'Day to day: acknowledge alerts, no configuration.'
                        : 'Read-only across the tenant.'
              }
            >
              <select id="invite-role" value={role} onChange={(e) => setRole(e.target.value)}>
                {roles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </Field>
            <div className="row">
              <Button type="submit" variant="primary" busy={busy} disabled={email.trim() === ''}>
                Create invitation
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <>
            <EmailOutcome email={sent} to={email} />
            <CopyLink token={token} />
            <div className="row">
              <Button onClick={onClose}>Done</Button>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}

// ── Tenants on the platform ──────────────────────────────────────────────

interface TenantRow {
  id: string;
  slug: string;
  name: string;
  status: string;
  requireMfa: boolean;
  createdAt: string;
}

export function PlatformTenants(): React.JSX.Element {
  const [rows, setRows] = useState<TenantRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows((await api.get<{ tenants: TenantRow[] }>('/api/platform/tenants')).tenants);
      setError(null);
    } catch (err) {
      setError(failure(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo<ColumnDef<TenantRow, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Tenant',
        accessorKey: 'name',
        cell: ({ row }) => (
          <div>
            <div className="cell-strong">{row.original.name}</div>
            <div className="small muted mono">{row.original.slug}</div>
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => (
          <Pill state={row.original.status === 'active' ? 'ok' : 'stale'}>{row.original.status}</Pill>
        ),
        size: 100,
      },
      {
        id: 'mfa',
        header: 'MFA',
        accessorFn: (r) => (r.requireMfa ? 1 : 0),
        cell: ({ row }) => <span className="small muted">{row.original.requireMfa ? 'required' : 'optional'}</span>,
        size: 100,
      },
      {
        id: 'created',
        header: 'Created',
        accessorKey: 'createdAt',
        cell: ({ row }) => <span className="small muted">{new Date(row.original.createdAt).toLocaleDateString()}</span>,
        size: 110,
      },
    ],
    [],
  );

  return (
    <>
      <Section
        flush
        title="Tenants"
        summary={rows === null ? undefined : `${rows.length} on this platform`}
        actions={
          <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setCreating(true)}>
            New tenant
          </Button>
        }
      >
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {rows === null ? (
          <Loading rows={2} cols={4} label="Loading tenants" />
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            getRowId={(r) => r.id}
            initialSort={[{ id: 'created', desc: false }]}
            caption="Tenants on this platform"
            empty={<Empty title="No tenants" />}
          />
        )}
      </Section>
      <PlatformAdmins />
      {creating && <NewTenantDrawer onClose={() => setCreating(false)} onCreated={() => void load()} />}
    </>
  );
}

function NewTenantDrawer({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }): React.JSX.Element {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [sent, setSent] = useState<InviteEmail | null>(null);

  const suggested = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  const effectiveSlug = slugTouched ? slug : suggested;

  return (
    <Drawer open onClose={onClose} title="New tenant" subtitle="Its owner is invited and chooses their own password.">
      <div className="form-narrow">
        {token === null ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError(null);
              try {
                const res = await api.post<{ token: string; email?: InviteEmail }>('/api/platform/tenants', {
                  name,
                  slug: effectiveSlug,
                  ownerEmail,
                });
                setToken(res.token);
                setSent(res.email ?? null);
                onCreated();
              } catch (err) {
                setError(failure(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            {error !== null && <Banner tone="fault">{error}</Banner>}
            <Field id="tenant-name" label="Name">
              <input id="tenant-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field id="tenant-slug" label="Slug" hint="Appears in URLs and cannot be changed later.">
              <input
                id="tenant-slug"
                type="text"
                className="mono"
                value={effectiveSlug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
              />
            </Field>
            <Field id="tenant-owner" label="Owner's email address">
              <input id="tenant-owner" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
            </Field>
            <div className="row">
              <Button
                type="submit"
                variant="primary"
                busy={busy}
                disabled={name.trim() === '' || effectiveSlug === '' || ownerEmail.trim() === ''}
              >
                Create tenant
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <>
            <Banner tone="ok" title={`${name} created`}>
              {ownerEmail} becomes its owner by opening the invitation link.
            </Banner>
            <EmailOutcome email={sent} to={ownerEmail} />
            <CopyLink token={token} />
            <div className="row">
              <Button onClick={onClose}>Done</Button>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}

function PlatformAdmins(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: 'ok' | 'fault'; text: string } | null>(null);

  const change = async (isPlatformAdmin: boolean): Promise<void> => {
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post<{ changed: boolean; consequence?: string; sessionsEnded?: number }>(
        '/api/platform/admins',
        { email, isPlatformAdmin },
      );
      setResult({
        tone: 'ok',
        text: !res.changed
          ? `Nothing to change: ${email} ${isPlatformAdmin ? 'already is' : 'is not'} a platform administrator.`
          : isPlatformAdmin
            ? `${email} is now a platform administrator. ${res.consequence ?? ''} Signed out of ${res.sessionsEnded ?? 0} session(s).`
            : `${email} is no longer a platform administrator. Signed out of ${res.sessionsEnded ?? 0} session(s).`,
      });
    } catch (err) {
      setResult({ tone: 'fault', text: failure(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      title="Platform administrators"
      summary="Can create tenants and change the collector's ceilings. Must use MFA."
    >
      <div className="form-narrow">
        {result !== null && <Banner tone={result.tone}>{result.text}</Banner>}
        <Field id="platform-admin-email" label="Account email address">
          <input
            id="platform-admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <div className="row">
          <Button busy={busy} disabled={email.trim() === ''} onClick={() => void change(true)}>
            Make platform administrator
          </Button>
          <Button variant="ghost" busy={busy} disabled={email.trim() === ''} onClick={() => void change(false)}>
            Remove
          </Button>
        </div>
      </div>
    </Section>
  );
}
